import React, { useMemo, useState } from 'react';
import {
  LuBarChart3,
  LuFactory,
  LuLandmark,
  LuLayoutDashboard,
  LuPackage,
  LuPrinter,
  LuSettings,
  LuShoppingCart,
  LuStore,
  LuUsers,
  LuWallet,
  LuWarehouse,
} from 'react-icons/lu';
import './Sidebar.css';
import { useLanguage } from './i18n/LanguageContext';
import { canModerateBranch, canAccessSettings, normalizeRole } from './utils/userLocale';
import { filterSubmenu, hasTenantModule, menuKeyAllowed } from './utils/tenantModules';
import { isAutomotiveBusiness, isBrickFactoryUser, isPharmacyUser, showsShopCustomerDocs } from './utils/businessType';

/** Main menu keys → Lucide icons (stroke style, consistent weight). */
const MAIN_MENU_ICONS = {
  overview: LuLayoutDashboard,
  users: LuUsers,
  orders: LuShoppingCart,
  stock: LuPackage,
  'auto-opening-stock': LuPackage,
  'auto-inventory': LuWarehouse,
  'auto-stock': LuWarehouse,
  'factory-stock': LuWarehouse,
  pos: LuStore,
  printing: LuPrinter,
  expenses: LuWallet,
  cash: LuLandmark,
  reports: LuBarChart3,
  settings: LuSettings,
  brick: LuFactory,
};

function SidebarMainIcon({ menuKey }) {
  const Cmp = MAIN_MENU_ICONS[menuKey];
  if (!Cmp) return null;
  return <Cmp className="sidebar-main-icon" size={20} aria-hidden />;
}

/** Submenu keys → emoji (main menu stays Lucide). */
const SUBMENU_EMOJI = {
  'branches-list': '🏢',
  'users-list': '👥',
  'users-add': '➕',
  'raw-materials-overview': '📊',
  'add-raw-material': '➕',
  'add-raw-material-category': '🏷️',
  'factory-suppliers': '🚚',
  'orders-make': '🛒',
  'orders-tracking': '📋',
  'orders-list': '📋',
  'shop-customer-docs': '📄',
  'factory-stock-overview': '📦',
  'stock-alerts': '⚠️',
  'stock-add': '➕',
  'stock-list': '📋',
  'stock-pending': '⏳',
  'pharmacy-expiry': '📅',
  'brick-catalog': '🧱',
  'brick-production': '🏭',
  'damaged-products': '💔',
  'pos-sale-products': '🛒',
  'brick-pre-orders': '📝',
  'brick-deliveries': '🚚',
  'pos-sale-history': '📜',
  'debts-list': '💳',
  'pos-other-services': '🧾',
  printing: '🖨️',
  'printing-new-job': '➕',
  'printing-payments': '💰',
  'printing-history': '📜',
  'printing-rates': '🏷️',
  'expenses-summary': '📊',
  'expenses-add': '➕',
  cash: '💰',
  'cash-new': '➕',
  'cash-insights': '📈',
  'cash-sms-monitor': '📱',
  'cash-history': '📜',
  'report-daily': '📅',
  'report-financial': '📊',
  'report-weekly': '📆',
  'report-monthly': '📊',
  'report-printing': '🖨️',
  'report-stock': '📦',
  'report-wakala': '💰',
  'report-loan-pack': '🏦',
  settings: '⚙️',
  'business-settings': '🏢',
  'settings-notifications': '🔔',
  subscription: '💳',
  'settings-account': '🔐',
  'security-password': '🔒',
  'auto-manual': '📖',
  'auto-products-record': '🧾',
  'auto-products-opening': '📥',
  'auto-products-categories': '🏷️',
  'auto-products-brands': '🛠️',
  'auto-products-units': '📐',
  'auto-products-list': '📋',
  'auto-inventory': '📦',
  'auto-suppliers': '🚚',
};

function SubmenuIcon({ subKey }) {
  const emoji = SUBMENU_EMOJI[subKey];
  if (!emoji) return null;
  return (
    <span className="submenu-item__icon submenu-item__icon--emoji" aria-hidden>
      {emoji}
    </span>
  );
}

function buildModules(currentUser, t) {
  const role = normalizeRole(currentUser?.role);
  const factory = isBrickFactoryUser(currentUser);
  const automotive = isAutomotiveBusiness(currentUser);
  const sub = (key) => t(`submenu.${key}`);
  const list = [
    { label: t('menu.overview'), key: 'overview' },
  ];
  if (role === 'owner') {
    list.push({
      label: t('menu.users'),
      key: 'users',
      submenu: [
        { label: sub('branches-list'), key: 'branches-list' },
        { label: sub('users-list'), key: 'users-list' },
        { label: sub('users-add'), key: 'users-add' },
      ],
    });
  } else if (hasTenantModule(currentUser, 'users')) {
    list.push({
      label: t('menu.usersBranch'),
      key: 'users',
      submenu: [{ label: sub('users-listBranch'), key: 'users-list' }],
    });
  }

  if (factory) {
    list.push({
      label: t('menu.stock'),
      key: 'stock',
      submenu: [
        { label: sub('raw-materials-overview'), key: 'raw-materials-overview' },
        { label: sub('add-raw-material'), key: 'add-raw-material' },
        { label: sub('add-raw-material-category'), key: 'add-raw-material-category' },
      ],
    });
    list.push({
      label: t('menu.orders'),
      key: 'orders',
      submenu: [
        { label: sub('factory-suppliers'), key: 'factory-suppliers' },
        { label: sub('orders-make'), key: 'orders-make' },
        { label: sub('orders-tracking'), key: 'orders-tracking' },
      ],
    });
    list.push({
      label: t('menu.factoryStock'),
      key: 'factory-stock',
      submenu: [
        { label: sub('factory-stock-overview'), key: 'factory-stock-overview' },
        { label: sub('stock-alerts'), key: 'stock-alerts' },
      ],
    });
  } else {
    if (automotive) {
      list.push(
        {
          label: t('menu.productsAuto'),
          key: 'stock',
          submenu: [
            { label: sub('auto-products-brands'), key: 'auto-products-brands' },
            { label: sub('auto-products-units'), key: 'auto-products-units' },
            { label: sub('auto-products-categories'), key: 'auto-products-categories' },
            { label: sub('auto-products-record'), key: 'auto-products-record' },
            { label: sub('auto-products-list'), key: 'stock-list' },
          ],
        },
        {
          label: t('menu.openingStockAuto'),
          key: 'auto-opening-stock',
        },
        {
          label: t('menu.stockAuto'),
          key: 'auto-stock',
          submenu: [
            { label: sub('auto-inventory'), key: 'auto-inventory' },
            { label: sub('damaged-productsAuto'), key: 'damaged-products' },
          ],
        },
        {
          label: t('menu.ordersAuto'),
          key: 'orders',
          submenu: [
            { label: sub('auto-suppliers'), key: 'auto-suppliers' },
            { label: sub('orders-makeAuto'), key: 'orders-make' },
            { label: sub('orders-trackingAuto'), key: 'orders-tracking' },
            ...(showsShopCustomerDocs(currentUser) ? [{ label: sub('shop-customer-docs'), key: 'shop-customer-docs' }] : []),
          ],
        }
      );
    } else {
      list.push(
        {
          label: t('menu.ordersShop'),
          key: 'orders',
          submenu: [
            {
              label: sub('orders-makeShop'),
              key: 'orders-make',
            },
            { label: sub('orders-list'), key: 'orders-list' },
            ...(showsShopCustomerDocs(currentUser) ? [{ label: sub('shop-customer-docs'), key: 'shop-customer-docs' }] : []),
          ],
        },
      ...(!automotive
        ? [
            {
              label: t('menu.stockShop'),
              key: 'stock',
              submenu: [
                { label: sub('stock-add'), key: 'stock-add' },
                { label: sub('stock-list'), key: 'stock-list' },
                ...(role === 'owner' || role === 'manager' ? [{ label: sub('stock-pending'), key: 'stock-pending' }] : []),
                { label: sub('stock-alerts'), key: 'stock-alerts' },
                ...(isPharmacyUser(currentUser)
                  ? [{ label: sub('pharmacy-expiry'), key: 'pharmacy-expiry' }]
                  : []),
              ],
            },
          ]
        : [])
      );
    }
  }

  list.push(
    ...(hasTenantModule(currentUser, 'brick')
      ? [
          {
            label: t('menu.brick'),
            key: 'brick',
            submenu: [
              { label: sub('brick-catalog'), key: 'brick-catalog' },
              { label: sub('brick-production'), key: 'brick-production' },
              { label: sub('damaged-products'), key: 'damaged-products' },
            ],
          },
        ]
      : []),
    {
      label: factory ? t('menu.posFactory') : automotive ? t('menu.posAuto') : t('menu.pos'),
      key: 'pos',
      submenu: [
        {
          label: automotive ? sub('pos-sale-productsAuto') : sub('pos-sale-products'),
          key: 'pos-sale-products',
        },
        ...(hasTenantModule(currentUser, 'brick')
          ? [
              { label: sub('brick-pre-orders'), key: 'brick-pre-orders' },
              {
                label: factory ? sub('brick-deliveries') : sub('brick-deliveriesShop'),
                key: 'brick-deliveries',
              },
            ]
          : []),
        { label: automotive ? sub('pos-sale-historyAuto') : sub('pos-sale-history'), key: 'pos-sale-history' },
        ...(hasTenantModule(currentUser, 'debts')
          ? [{ label: automotive ? sub('debts-listAuto') : sub('debts-list'), key: 'debts-list' }]
          : []),
        ...(hasTenantModule(currentUser, 'other_services')
          ? [{ label: sub('pos-other-services'), key: 'pos-other-services' }]
          : []),
      ],
    },
    ...(!factory && hasTenantModule(currentUser, 'printing')
      ? [
          {
            label: t('menu.printing'),
            key: 'printing',
            submenu: [
              { label: sub('printing'), key: 'printing' },
              { label: sub('printing-new-job'), key: 'printing-new-job' },
              { label: sub('printing-payments'), key: 'printing-payments' },
              { label: sub('printing-history'), key: 'printing-history' },
              ...(role === 'owner' ? [{ label: sub('printing-rates'), key: 'printing-rates' }] : []),
            ],
          },
        ]
      : []),
    ...(canModerateBranch(role)
      ? [
          {
            label: factory ? t('menu.expensesFactory') : t('menu.expenses'),
            key: 'expenses',
            submenu: [
              { label: sub('expenses-summary'), key: 'expenses-summary' },
              { label: sub('expenses-add'), key: 'expenses-add' },
            ],
          },
        ]
      : []),
    ...(!factory && hasTenantModule(currentUser, 'cashbook')
      ? [
          {
            label: t('menu.cash'),
            key: 'cash',
            submenu: [
              { label: sub('cash'), key: 'cash' },
              { label: sub('cash-new'), key: 'cash-new' },
              ...(role === 'owner' ? [{ label: sub('cash-insights'), key: 'cash-insights' }] : []),
              ...(role === 'owner' ? [{ label: sub('cash-sms-monitor'), key: 'cash-sms-monitor' }] : []),
              { label: sub('cash-history'), key: 'cash-history' },
            ],
          },
        ]
      : [])
  );
  const reportSubmenu = [
    { label: sub('report-financial'), key: 'report-financial' },
    { label: sub('report-daily'), key: 'report-daily' },
    { label: sub('report-weekly'), key: 'report-weekly' },
    { label: sub('report-monthly'), key: 'report-monthly' },
    ...(hasTenantModule(currentUser, 'printing')
      ? [{ label: sub('report-printing'), key: 'report-printing' }]
      : []),
    { label: sub('report-stock'), key: 'report-stock' },
    ...(hasTenantModule(currentUser, 'cashbook')
      ? [{ label: sub('report-wakala'), key: 'report-wakala' }]
      : []),
    ...(canModerateBranch(role)
      ? [{ label: sub('report-loan-pack'), key: 'report-loan-pack' }]
      : []),
  ];

  if (role === 'owner' || role === 'manager') {
    list.push({
      label: t('menu.reports'),
      key: 'reports',
      submenu: reportSubmenu,
    });
  }

  if (automotive) {
    list.push({ label: t('menu.manualAuto'), key: 'auto-manual' });
  }

  if (canAccessSettings(role)) {
    list.push({ label: t('menu.settings'), key: 'settings' });
  }
  return list
    .filter((mod) => menuKeyAllowed(mod.key, currentUser))
    .map((mod) => {
      if (!mod.submenu) return mod;
      const submenu = filterSubmenu(mod.submenu, currentUser);
      if (!submenu.length) return null;
      return { ...mod, submenu };
    })
    .filter(Boolean);
}

const Sidebar = ({ onNavigate, currentUser, sidebarOpen, setSidebarOpen }) => {
  const { t } = useLanguage();
  const modules = useMemo(() => buildModules(currentUser, t), [currentUser, t]);

  const [open, setOpen] = useState({
    stock: false,
    reports: false,
    pos: false,
    users: false,
    cash: false,
    printing: false,
    settings: false,
    brick: false,
    expenses: false,
    'auto-stock': false,
  });
  const [activeMenu, setActiveMenu] = useState(null);

  const isMobile = () => window.innerWidth <= 900;

  const handleMenuClick = (key, hasSubmenu) => {
    if (hasSubmenu) {
      setOpen(o => ({ ...o, [key]: !o[key] }));
      setActiveMenu(key);
    } else {
      onNavigate(key);
      setActiveMenu(key);
      // Only auto-close sidebar on mobile
      if (isMobile() && typeof setSidebarOpen === 'function') setSidebarOpen(false);
    }
  };

  const handleSubmenuClick = (subKey) => {
    onNavigate(subKey);
    setActiveMenu(subKey);
    if (isMobile() && typeof setSidebarOpen === 'function') setSidebarOpen(false);
  };

  return (
    <div className="sidebar-shell">
      {sidebarOpen && isMobile() && (
        <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      )}
      <nav className={`sidebar ${sidebarOpen ? 'show' : 'hide'}`} aria-label="Main navigation">
        <ul>
          {modules.map(mod => (
            <li
              key={mod.key}
              className={
                (mod.submenu ? 'has-submenu ' : '') +
                (activeMenu === mod.key ? 'active' : '')
              }
            >
              <div
                className="sidebar-link"
                onClick={() => handleMenuClick(mod.key, !!mod.submenu)}
              >
                <span className="sidebar-link__start">
                  <SidebarMainIcon menuKey={mod.key} />
                  <span className="sidebar-link__label">{mod.label}</span>
                </span>
                {mod.submenu ? (
                  <span className={`arrow${open[mod.key] ? ' open' : ''}`} aria-hidden />
                ) : null}
              </div>
              {mod.submenu && open[mod.key] && (
                <ul className="submenu">
                  {mod.submenu.map((sub, idx) =>
                    sub.divider ? (
                      <li key={`${sub.label}-${idx}`} className="submenu-divider" aria-hidden>
                        {sub.label}
                      </li>
                    ) : (
                      <li
                        key={sub.key}
                        className={[
                          'submenu-item',
                          activeMenu === sub.key ? 'submenu-item--active' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onClick={() => handleSubmenuClick(sub.key)}
                      >
                        <SubmenuIcon subKey={sub.key} />
                        <span className="submenu-item__label">{sub.label}</span>
                      </li>
                    )
                  )}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
};

export default Sidebar;
